import { Link } from "react-router-dom";
import { ArrowRight, Award, Compass, ShieldCheck, Users2 } from "lucide-react";
import SectionHeader from "../components/SectionHeader";
import { ConsultationDialog } from "../components/ConsultationDialog";

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="pt-32">
      {/* Hero */}
      <section className="relative pb-20 pt-12">
        <div className="container-myc grid lg:grid-cols-12 gap-14 items-end">
          <div className="lg:col-span-7">
            <p className="eyebrow">About Moneyyard Capital</p>
            <h1 className="h-display-1 text-[#2A0A0E] mt-6">
              Managing wealth with
              <br />
              <span className="italic font-normal text-[#7A1F26]">
                integrity, expertise,
              </span>{" "}
              and long-term financial vision.
            </h1>
          </div>
          <div className="lg:col-span-5 lg:pl-10 lg:border-l border-[#7A1F26]/30">
            <p className="text-[#2A0A0E]/75 leading-relaxed">
              At Moneyyard, we provide trusted, personalized wealth management built on long-term relationships, helping clients grow, protect, and preserve their wealth across generations while creating value beyond wealth.
            </p>
          </div>
        </div>
      </section>

      {/* Image strip */}
      <section className="container-myc">
        <div className="relative">
          <img
            src="https://images.unsplash.com/photo-1486325212027-8081e485255e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Modern global skyline"
            alt="Moneyyard Capital — Manhattan-style financial markets skyline"
            className="w-full h-[420px] object-cover rounded-2xl shadow-xl"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF] via-transparent to-transparent" />
        </div>
      </section>

      {/* Story */}
      <section className="py-24 lg:py-28">
        <div className="container-myc grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="eyebrow">Our story</p>
            <h2 className="h-display-2 text-[#2A0A0E] mt-4">
              Wealth, considered.
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-slate-600/70 leading-relaxed">
            <p>
              Headquartered in BKC, Mumbai, Moneyyard Capital is an
              independent fiduciary firm built on trust and understanding,
              offering transparent, long-term financial guidance.
            </p>
            <p>
              We focus on disciplined, diversified investing to build
              personalized portfolios — chosen on merit, not commission. Each
              engagement begins with understanding your goals, risk tolerance
              and constraints before a single rupee is allocated.
            </p>
            <p>
              Beyond the portfolio, we maintain strong client relationships
              through ongoing advice and support for key financial decisions —
              from tax events and liquidity moments to estate transitions —
              so you have a trusted partner across every stage of wealth.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-[#FAF5EB]">
        <div className="container-myc">
          <SectionHeader
            eyebrow="Our values"
            title="Four principles that guide every recommendation."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-900/5 mt-14 border border-slate-200/5">
            {[
              { i: ShieldCheck, t: "Fiduciary first", d: "We sit on your side of the table — always." },
              { i: Compass, t: "Process over prediction", d: "Repeatable frameworks beat hot calls." },
              { i: Users2, t: "Family-aligned", d: "Wealth is for generations, not quarters." },
              { i: Award, t: "Earned trust", d: "Long tenures built on outcomes and transparency." },
            ].map(({ i: Icon, t, d }) => (
              <div key={t} className="bg-[#FAF5EB] p-8 hover:bg-[#FAF5EB] transition-colors">
                <Icon className="w-6 h-6 text-[#7A1F26]" />
                <h3 className="font-display text-xl text-[#2A0A0E] mt-6">{t}</h3>
                <p className="text-sm text-slate-600/55 mt-3 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-myc text-center max-w-2xl mx-auto">
          <h2 className="h-display-2 text-[#2A0A0E]">
            Want to know if we are the right fit?
          </h2>
          <p className="text-slate-600/65 mt-5">
            A 30-minute conversation is the simplest way to find out.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <ConsultationDialog
              trigger={
                <button data-testid="about-cta-consult-button" className="btn-gold">
                  Book a Consultation <ArrowRight className="w-4 h-4" />
                </button>
              }
            />
            <Link to="/contact" className="btn-outline-gold" data-testid="about-cta-contact-link">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
